import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  STATUSNAME,
  type Angebotsstatus,
  type Angebotszeile,
  liste,
} from '../api/angebote';
import { ApiFehler } from '../api/fehler';
import { markenklasse, type Markenart } from '../marken';
import { Meldung } from '../komponenten/Meldung';
import type { Filtermarke } from '../komponenten/Filtermarken';
import { Liste } from '../komponenten/Liste';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { KundeVerweis } from '../komponenten/Kontaktkarte';
import { alsEuro } from '../betrag';
import { alsTag } from '../datum';

type Filter = {
  readonly suche: string;
  readonly status: Angebotsstatus | '';
  readonly alleVersionen: boolean;
};

const LEER: Filter = { suche: '', status: '', alleVersionen: false };

/**
 * Welche Marke ein Status traegt.
 *
 * WCAG 1.4.1: Der Zustand steht als Text da, die Farbe kommt hinzu. Ein
 * abgelehntes und ein abgelaufenes Angebot sehen deshalb verschieden aus UND
 * heissen verschieden — wer die Farben nicht unterscheidet, liest es.
 */
export const STATUSMARKE: Readonly<Record<Angebotsstatus, Markenart>> = {
  entwurf: 'stillgelegt',
  /* Hinausgegangen heisst nicht gewonnen — das Angebot laeuft. */
  versendet: 'laufend',
  verhandlung: 'laufend',
  angenommen: 'abschluss',
  abgelehnt: 'sackgasse',
  /*
   * ABGELAUFEN IST KEINE ABLEHNUNG, und seit heute sieht man das auch: Der
   * Kommentar darueber behauptete es schon, beide trugen aber dieselbe rote
   * Flaeche. Eine abgelaufene Frist verlangt eine Handlung (nachfassen, neue
   * Version); ein abgelehntes Angebot ist zu Ende.
   */
  abgelaufen: 'aufmerksamkeit',
};

/**
 * Die Angebotsliste — der Einstieg in den Bereich „Angebote".
 *
 * Seitentyp Liste (ÄA-41): Zähler und „Neues Angebot" im Seitenkopf, darunter
 * die Filterleiste und die Rastertabelle — derselbe Baustein wie bei den
 * Kunden. Dasselbe Personal bedient beide.
 *
 * ZWEI DINGE SIND HIER ANDERS, und beide haengen an ANG-15:
 *
 *   * Sie zeigt je Vorgang NUR DIE AKTUELLE FASSUNG. Drei Versionen
 *     desselben Angebots waeren drei Zeilen mit derselben Nummer — der
 *     Anwender oeffnete die falsche. Der Schalter „alle Fassungen" macht
 *     sie sichtbar, wenn jemand sie sucht.
 *   * Die VERSION steht neben der Nummer, sobald sie ueber 1 liegt. Bei
 *     Version 1 stuende sie nur im Weg.
 *
 * Die BRUTTOSUMME kommt vom Server und wird hier nicht gerechnet: Eine
 * Summe im Browser waere die zweite Wahrheit neben dem Rechenkern.
 */
export function Angebotsliste({
  zurKunde,
  beiOeffnen,
  beiNeu,
}: {
  /** CRM-181: der Weg zur Kundenakte, nur mit `kunde:sehen` — zugleich das Recht der Kontaktkarte. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  readonly beiOeffnen: (id: string) => void;
  /**
   * Nur mit `angebot:anlegen` (SYS-01, BEN-02; Auftrag #32). Wer Angebote nur sieht —
   * Buchhaltung, Geschaeftsleitung, Operations —, bekommt keinen Knopf, der
   * mit 403 endet.
   */
  readonly beiNeu?: (() => void) | undefined;
}): JSX.Element {
  const [filter, setzeFilter] = useState<Filter>(LEER);
  const [angebote, setzeAngebote] = useState<readonly Angebotszeile[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const laden = useCallback(async (f: Filter): Promise<void> => {
    setzeLaedt(true);
    try {
      setzeAngebote(
        await liste({ suche: f.suche, status: f.status, alleVersionen: f.alleVersionen }),
      );
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  // 250 Millisekunden Ruhe genuegen, damit ein Wort zu Ende getippt ist —
  // dieselbe Zahl wie in den anderen Listen.
  useEffect(() => {
    const kennung = setTimeout(() => void laden(filter), 250);
    return () => clearTimeout(kennung);
  }, [filter, laden]);

  const marken: readonly Filtermarke[] = [
    ...(filter.status !== ''
      ? [
          {
            text: `Status: ${STATUSNAME[filter.status]}`,
            entfernen: () => setzeFilter((f) => ({ ...f, status: '' })),
          },
        ]
      : []),
    ...(filter.alleVersionen
      ? [
          {
            text: 'alle Fassungen',
            entfernen: () => setzeFilter((f) => ({ ...f, alleVersionen: false })),
          },
        ]
      : []),
  ];

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(laedt ? undefined : zaehlwort(angebote.length, 'Angebot', 'Angebote'));

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      <Liste
        suche={{
          wert: filter.suche,
          aendern: (wert) => setzeFilter((f) => ({ ...f, suche: wert })),
          beschriftung: 'Angebote suchen',
          platzhalter: 'Angebotsnummer oder Betreff',
        }}
        filter={
          <>
            <select
              className="liste__filter"
              value={filter.status}
              onChange={(e) =>
                setzeFilter((f) => ({ ...f, status: e.target.value as Angebotsstatus | '' }))
              }
              aria-label="Status"
            >
              <option value="">Alle Status</option>
              {(Object.keys(STATUSNAME) as Angebotsstatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUSNAME[s]}
                </option>
              ))}
            </select>
            {/* ANG-15 */}
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={filter.alleVersionen}
                onChange={(e) => setzeFilter((f) => ({ ...f, alleVersionen: e.target.checked }))}
              />
              alle Fassungen
            </label>
          </>
        }
        marken={marken}
        hinweise={
          <p className="hilfe">
            Gezeigt wird je Vorgang die aktuelle Fassung. Ältere Versionen bleiben unverändert
            einsehbar — der Schalter oben holt sie in die Liste.
          </p>
        }
      >
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <caption className="nur-vorlesen">{angebote.length} Angebote</caption>
            <thead>
              <tr>
                <th scope="col">Nummer</th>
                <th scope="col">Kunde</th>
                <th scope="col">Betreff</th>
                <th scope="col">gültig bis</th>
                <th scope="col">Status</th>
                <th scope="col" className="zahl">
                  Brutto
                </th>
              </tr>
            </thead>
            <tbody>
              {angebote.map((a) => (
                <tr key={a.id}>
                  <td>
                    <button
                      type="button"
                      className="liste__oeffnen liste__nummer"
                      onClick={() => beiOeffnen(a.id)}
                    >
                      {a.angebotsnummer}
                    </button>
                    {a.version > 1 && (
                      <span className="kanalliste__name"> · V{a.version}</span>
                    )}
                  </td>
                  <td className="liste__name">
                  <KundeVerweis kundeId={a.kunde} zurAkte={zurKunde}>
                    {a.kundenname}
                  </KundeVerweis>
                </td>
                  <td>{a.betreff}</td>
                  <td className="liste__einzeilig">{alsTag(a.gueltigBis)}</td>
                  <td>
                    <span className={markenklasse(STATUSMARKE[a.status])}>
                      {STATUSNAME[a.status]}
                    </span>
                  </td>
                  <td className="zahl">{alsEuro(a.belegbrutto)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!laedt && angebote.length === 0 && (
          <div className="liste__leer" role="status">
            <p>
              {filter.suche === '' && filter.status === ''
                ? 'Noch kein Angebot.'
                : `Kein Treffer${filter.suche === '' ? '' : ` für „${filter.suche}“`}.`}
            </p>
            {beiNeu !== undefined && (
              <button type="button" className="knopf knopf--haupt" onClick={beiNeu}>
                Angebot anlegen
              </button>
            )}
          </div>
        )}
      </Liste>
    </>
  );
}
